import ImportedSourceData from '../components/ImportedSourceData';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Security as CertificateIcon,
  Security,
  Dns as DnsIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { certificateService } from '../services/api';
import Layout from '../components/Layout/Layout';

const CertificateList = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selfSignedFilter, setSelfSignedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('cards');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCertificates();
  }, []);

  useEffect(() => {
    filterCertificates();
  }, [certificates, searchTerm, statusFilter, typeFilter, selfSignedFilter]);

  const loadCertificates = async () => {
    try {
      const response = await certificateService.getCertificates();
      setCertificates(response.certificates || []);
    } catch (error) {
      console.error('Erro ao carregar certificados:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar certificados',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const filterCertificates = () => {
    let filtered = certificates;

    // Filtro por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(certificate =>
        certificate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.dns_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.certificate_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.document_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.document_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.entity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        certificate.certificadora?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(certificate => certificate.status === statusFilter);
    }

    // Filtro por tipo
    if (typeFilter !== 'all') {
      filtered = filtered.filter(certificate => certificate.certificate_type === typeFilter);
    }

    // Filtro por auto-assinado
    if (selfSignedFilter !== 'all') {
      filtered = filtered.filter(certificate => certificate.is_self_signed === (selfSignedFilter === 'true'));
    }

    setFilteredCertificates(filtered);
  };

  const handleEdit = (certificateId) => {
    navigate(`/certificates/${certificateId}/edit`);
  };

  const handleDelete = async (certificateId) => {
    if (window.confirm('Tem certeza que deseja excluir este certificado?')) {
      try {
        await certificateService.deleteCertificate(certificateId);
        setSnackbar({
          open: true,
          message: 'Certificado excluído com sucesso',
          severity: 'success'
        });
        loadCertificates();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || 'Erro ao excluir certificado',
          severity: 'error'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'expired': return 'error';
      case 'revoked': return 'error';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '🟢 Ativo';
      case 'inactive': return '🔴 Inativo';
      case 'expired': return '⏰ Expirado';
      case 'revoked': return '🚫 Revogado';
      case 'maintenance': return '🟡 Manutenção';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'expired': return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'revoked': return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'maintenance': return <WarningIcon sx={{ color: 'warning.main' }} />;
      default: return <CertificateIcon />;
    }
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
          <Typography variant="h4" gutterBottom>
            Carregando...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
              <CertificateIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                Gerenciamento de Certificados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredCertificates.length} certificados encontrados
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/certificates/new')}
          >
            Novo Certificado
          </Button>
        </Box>

        {/* Filtros e Busca */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Buscar certificados..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                  <MenuItem value="expired">Expirado</MenuItem>
                  <MenuItem value="revoked">Revogado</MenuItem>
                  <MenuItem value="maintenance">Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="SSL/TLS">SSL/TLS</MenuItem>
                  <MenuItem value="Wildcard">Wildcard</MenuItem>
                  <MenuItem value="Multi-domain">Multi-domain</MenuItem>
                  <MenuItem value="EV (Extended Validation)">EV</MenuItem>
                  <MenuItem value="Code Signing">Code Signing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Auto-assinado</InputLabel>
                <Select
                  value={selfSignedFilter}
                  onChange={(e) => setSelfSignedFilter(e.target.value)}
                  label="Auto-assinado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="true">Sim</MenuItem>
                  <MenuItem value="false">Não</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('cards')}
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                >
                  Tabela
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de Certificados */}
        {viewMode === 'cards' ? (
          <Grid container spacing={3}>
            {filteredCertificates.map((certificate) => (
              <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                        {getStatusIcon(certificate.status)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {certificate.name}
                        </Typography>
                          <ImportedSourceData product={certificate} />
                        <Chip
                          label={getStatusLabel(certificate.status)}
                          color={getStatusColor(certificate.status)}
                          size="small"
                        />
                      </Box>
                    </Box>

                    {certificate.certificate_type && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <Security sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        {certificate.certificate_type}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {/* Campos específicos de certificados digitais */}
                      {certificate.document_type && certificate.document_id && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          📄 {certificate.document_type}: {certificate.document_id}
                        </Typography>
                      )}

                      {certificate.entity && (
                        <Typography variant="body2">
                          🏢 {certificate.entity}
                        </Typography>
                      )}

                      {certificate.certificadora && (
                        <Typography variant="body2">
                          🏛️ Certificadora: {certificate.certificadora}
                        </Typography>
                      )}

                      {certificate.certificate_model && (
                        <Typography variant="body2">
                          💾 Modelo: {certificate.certificate_model}
                        </Typography>
                      )}

                      {certificate.dns_name && (
                        <Typography variant="body2">
                          <DnsIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          {certificate.dns_name}{certificate.dns_suffix && certificate.dns_suffix}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {certificate.is_self_signed ? (
                          <LockIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        ) : (
                          <LockOpenIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                        <Typography variant="body2">
                          {certificate.is_self_signed ? 'Auto-assinado' : 'Assinado por CA'}
                        </Typography>
                      </Box>

                      {certificate.serial_number && (
                        <Typography variant="body2">
                          <strong>Serial:</strong> {certificate.serial_number}
                        </Typography>
                      )}

                      {certificate.expiration_date && (
                        <Typography variant="body2" sx={{
                          color: isExpiringSoon(certificate.expiration_date) ? 'warning.main' : 'inherit'
                        }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          Expira: {new Date(certificate.expiration_date).toLocaleDateString('pt-BR')}
                          {isExpiringSoon(certificate.expiration_date) && (
                            <Chip label="Expira em breve" size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      )}

                      {certificate.location && (
                        <Typography variant="body2">
                          <LocationIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          {certificate.location}
                        </Typography>
                      )}

                      {certificate.technician_in_charge && (
                        <Typography variant="body2">
                          <PersonIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                          {certificate.technician_in_charge}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(certificate.id)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(certificate.id)}
                    >
                      Excluir
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo Documento</TableCell>
                  <TableCell>ID Documento</TableCell>
                  <TableCell>Entidade</TableCell>
                  <TableCell>Certificadora</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expiração</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCertificates.map((certificate) => (
                  <TableRow key={certificate.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{certificate.name}</Typography>
                        <ImportedSourceData product={certificate} />
                        {certificate.certificate_type && (
                          <Typography variant="caption" color="text.secondary">
                            Tipo: {certificate.certificate_type}
                          </Typography>
                        )}
                        {certificate.certificate_model && (
                          <Typography variant="caption" color="text.secondary">
                            Modelo: {certificate.certificate_model}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {certificate.document_type ? (
                        <Chip
                          label={certificate.document_type}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {certificate.document_id ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {certificate.document_id}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {certificate.entity || '-'}
                    </TableCell>
                    <TableCell>
                      {certificate.certificadora || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(certificate.status)}
                        color={getStatusColor(certificate.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {certificate.expiration_date ? (
                        <Box>
                          <Typography variant="body2" sx={{
                            color: isExpiringSoon(certificate.expiration_date) ? 'warning.main' : 'inherit'
                          }}>
                            {new Date(certificate.expiration_date).toLocaleDateString('pt-BR')}
                          </Typography>
                          {isExpiringSoon(certificate.expiration_date) && (
                            <Chip label="Expira em breve" size="small" color="warning" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(certificate.id)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(certificate.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredCertificates.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum certificado encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || selfSignedFilter !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Comece cadastrando seu primeiro certificado'
              }
            </Typography>
          </Paper>
        )}

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default CertificateList;
